import { getUserManager } from './oidc';

import type { SessionUser } from './session-store';

/**
 * Cognito id_token claim shape — only the keys this app maps. `user.profile` on the
 * oidc-client-ts `User` is the decoded id_token payload, so no network call is needed.
 */
type Claims = {
  sub?: string;
  email?: string;
  name?: string;
  'cognito:username'?: string;
  'custom:tenant_id'?: string;
};

/**
 * Builds the session identity from the id_token claims on the stored/exchanged user. Makes no
 * network request. Carries no roles and no permissions: the pool emits neither, and the identity
 * store is where both live (`profileWithPermissions` adds them). A missing `sub` means there is no
 * usable identity, which is terminal (see `adoptProfile` in `session-manager.ts`).
 */
export async function profileFromClaims(): Promise<SessionUser> {
  const user = await getUserManager().getUser();
  const claims = user?.profile as Claims | undefined;
  if (!claims?.sub) throw new Error('id_token has no subject claim');

  return {
    id: claims.sub,
    email: claims.email ?? '',
    displayName: claims.name ?? claims['cognito:username'] ?? claims.email ?? claims.sub,
    tenantId: claims['custom:tenant_id'],
    roles: [],
  };
}
